import type { UserLocationLog } from 'api/models/api';
import type {
  CustomActionEntity,
  HttpMethod,
  ServiceObjectsServiceObjectRevisionCategory,
  User
} from 'api/models/mainservice';
import type { AxiosError } from 'axios';
import type { Address, CustomerKind, PhoneNumber } from 'pages/customers/models';
import type { InterventionDetail, RelatedDataIntervention } from 'pages/interventions/models';
import type {
  CustomerInOrder,
  InvoiceCustomerInOrder,
  OrderDetail,
  TotalCostData
} from 'pages/orders/models';
import type { ServiceObjectInCustomer } from 'pages/serviceObjects/models';
import type { Codebooks } from 'pages/settings/codebooks/models';
import type { AddressSearchProvider, CompanyAddress } from 'pages/settings/companySettings/models';

export enum AsyncActionMode {
  REQUEST = 'REQUEST',
  RESPONSE = 'RESPONSE',
  ERROR = 'ERROR'
}

export interface AuthState {
  authenticated: boolean;
  loadingUser: boolean;
  error?: string;
  refreshTokenFailed: boolean;
  tenantCode?: string;
  historyStack: string[];
}

export interface SignInCredentials {
  tenantCode: string;
  username: string;
  password: string;
}

export interface StorageDownloadTemplates {
  attachment?: string | null;
  supportTicketAttachment?: string | null;
  excelImport?: string | null;
  excelExport?: string | null;
}
export interface LoginResult {
  success: boolean;
  error?: string | null;
  accessToken?: string | null;
  refreshToken?: string | null;
  deviceLocationToken?: string | null;
  attachmentDownloadUriTemplate?: string | null;
  downloadUriTemplates?: StorageDownloadTemplates | null;
  user?: AppUser | null;
}

export interface PublicCompanySettings {
  name: string;
  logoUrl?: string | null;
  isDemo: boolean;
  countryId: string;
  currencyId: string;
  languageId: string;
  timezoneId: string;
}

export interface ConfigurationItem {
  isUserSpecific: boolean;
  key: string;
  value: string;
}

export interface ParsedConfigurationItem {
  isUserSpecific: boolean;
  key: string;
  value?: Record<string, any>; // TODO: in future maybe we will handle also string
}

export interface ConfigurationItemToEdit {
  isCompanyDefault?: boolean;
  key: string;
  value?: string | null;
}

export interface UserConfigurationItemToEdit {
  isCompanyDefault?: boolean;
  key: string;
  value?: string | null;
}

export interface UpdateConfigurationRequest {
  configuration: ConfigurationItemToEdit[];
}

export interface CustomFormTemplate {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  isDeleted: boolean;
  name: string;
  template: CustomFormDefinition;
  /** @format int32 */
  sortOrder: number;
  hasMaterial: boolean;
  forAssignee: boolean;
  needsIntervention: boolean;
  needsServiceObject: boolean;
  multipleServiceObjects: boolean;
  orderCategoryRestrictions: string;
  serviceObjectCategoryRestrictions: string;
}

export interface CustomFormTemplateToEdit {
  /** @format int32 */
  sortOrder: number;
}

export interface LoggedUserCompany {
  name: string;
  customerKind: CustomerKind;
  tenantCode: string;
  identificationNumber?: string | null;
  taxNumber?: string | null;
  address: CompanyAddress;
  logoUrl?: string | null;
  headerLogoUrl?: string | null;
  stampUrl?: string | null;
  type: CompanyType;
  departureMode: DepartureMode;
  orderCodeTemplate?: string | null;
  reportEmails: string[];
  nextRevisionEmails: string[];
  autoConfirmation: boolean;
  singleAssigneeMode: boolean;
  requireTechnicianLocation: boolean;
  hideStores: boolean;
  disableManualCustomForms: boolean;
  disableManualCustomFormsForTechnician: boolean;
  noOverlaps: boolean;
  enableGroupsAndSkills: boolean;
  enableSlotSuggestion: boolean;
  restrictTechnicianDataVisibility: boolean;
  allowCustomerSendByPost: boolean;
  customerCustomDataTemplate?: CustomDataTemplate;
  orderCustomDataTemplate?: CustomDataTemplate;
  customFormTemplates: CustomFormTemplate[];
  /** @format double */
  travelFee: number;
  /** @format double */
  pricePerKilometer: number;
  maxTravelFeeMode: boolean;
  countryId: string;
  /** Always set - fallbacks to countrys default */
  currencyId: string;
  /** Always set - fallbacks to countrys default */
  languageId: string;
  /** Always set from country */
  timezoneId: string;
  /** @format int32 */
  workHoursStart: number;
  /** @format int32 */
  workHoursEnd: number;
  /** @format int32 */
  precision: number;
  /** @format int32 */
  defaultInterventionLength: number;
  /** @format int32 */
  nextRevisionTresholdInDays: number;
  /** @format uuid */
  nextRevisionCategoryId?: string | null;
  /**
   * This is set only for admin role
   * @format int32
   */
  activeUsersCount?: number | null;
  firstDayOfWeek: DayOfWeek;
  /** @format int32 */
  firstDayOfYear: number;
  configuration: ConfigurationItem[];
  isReportingEnabled: boolean;
  requireComplexPasswords: boolean;
  customerPortalEnabled: boolean;
  primaryColor: string;
  accentColor: string;
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  defaultAddressSearchProvider: AddressSearchProvider;
  allowContactPersonNotifications: boolean;
  allowContactPersonReports: boolean;
}

export type DepartureMode = 'Disabled' | 'Optional' | 'Required';

export const DepartureModeArray = ['Disabled', 'Optional', 'Required'];

export type CompanyType = 'Standard' | 'OneManShow' | 'MultiBranch';

export const CompanyTypeArray = ['Standard', 'OneManShow', 'MultiBranch'];

export interface LoggedUserInfo {
  user: AppUser;
  company: LoggedUserCompany;
  licence?: CurrentLicence | null;
}

export interface CurrentLicence {
  /** @format uuid */
  id: string;
  /** @format date-time */
  expiresAt?: string | null;
  /** @format int32 */
  value: number;
  type: LicenceType;
}

export interface Licence {
  id: string;
  isActive: boolean;
  isTrial: boolean;
  expiresAt?: string | null;
  value: number;
  type: LicenceType;
}

export interface LicenceToAdd {
  id?: string | null;
  companyId: string;
  expiresAt?: string | null;
  value: number;
  type: LicenceType;
  isTrial: boolean;
}

export interface AppUser {
  /** @format uuid */
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  /** @format uuid */
  companyId: string;
  tenantCode: string;
  companyName: string;
  email: string;
  hasEmailConfirmed: boolean;
  phone?: PhoneNumber | null;
  hasPhoneConfirmed: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isTechnician: boolean;
  isReadonly: boolean;
  sendNotificationsByPhone: boolean;
  sendNotificationsByEmail: boolean;
  isReportingEnabled: boolean;
  canAddCalendarEvents: boolean;
  canAddOrders: boolean;
  canAddCustomers: boolean;
  canPlanInterventionsToSelf: boolean;
  isDisabled: boolean;
  signatureUrl: string;
  userProfilePictureUrl?: string | null;
  userProfileColor?: string | null;
}

export interface Attachment {
  name: string;
  publicUrl: string;
  internalStorageId: string;
  /** @format uuid */
  orderId?: string | null;
  /** @format uuid */
  interventionId?: string | null;
  /** @format uuid */
  serviceObjectId?: string | null;
  /** @format uuid */
  orderCustomFormId?: string | null;
  /** @format uuid */
  orderRequestId?: string | null;
  /** @format uuid */
  customerId?: string | null;
  /** @format uuid */
  uploadedById?: string | null;
  description: string;
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  /** @format int64 */
  sizeBytes: number;
  md5Hash: string;
}

export interface AttachmentToEdit {
  name?: string | null;
  description?: string | null;
}

export interface AttachmentUploadQuery {
  serviceObjectId?: string | null;
  orderId?: string | null;
  interventionId?: string | null;
  orderCustomFormId?: string | null;
  orderRequestId?: string | null;
  customerId?: string | null;
}

export type CodebookType =
  | 'customerCategory'
  | 'customerType'
  | 'materialCategory'
  | 'materialSupplier'
  | 'orderCategory'
  | 'serviceObjectCategory'
  | 'serviceObjectCustomState'
  | 'serviceObjectManufacturer'
  | 'workItem'
  | 'vatOption'
  | 'interventionCategory'
  | 'interventionCustomState'
  | 'orderCustomState'
  | 'orderRequestCustomState'
  | 'paymentType'
  | 'calendarEventCategory'
  | 'orderRequestCategory';

export const CodebookTypeArray: CodebookType[] = [
  'customerCategory',
  'customerType',
  'materialCategory',
  'materialSupplier',
  'orderCategory',
  'interventionCategory',
  'interventionCustomState',
  'serviceObjectCategory',
  'serviceObjectCustomState',
  'serviceObjectManufacturer',
  'workItem',
  'vatOption',
  'orderCustomState',
  'orderRequestCustomState',
  'paymentType',
  'calendarEventCategory',
  'orderRequestCategory'
];

export interface Codebook {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name: string;
  slug?: string | null;
  /** @format int32 */
  sortOrder: number;
}

export interface CodebookToAdd {
  name: string;
  slug: string;
  /** @format int32 */
  sortOrder: number;
}

export interface CodebookToEdit {
  name: string;
  slug: string;
  /** @format int32 */
  sortOrder: number;
}

export interface Manufacturer extends Codebook {
  identificationNumber?: string | null;
}

export interface ManufacturerToAdd extends CodebookToAdd {
  identificationNumber?: string | null;
}

export interface ManufacturerToEdit extends CodebookToEdit {
  identificationNumber?: string | null;
}

export interface ServiceObjectCategory extends Codebook {
  customDataTemplate?: CustomDataTemplate | null;
  revisionCategoryId?: string | null;
}

export interface ServiceObjectCategoryToEdit extends CodebookToEdit {
  revisionCategoryId?: string | null;
}

export interface ServiceObjectCategoryWithStringTemplate extends Codebook {
  customDataTemplate?: string | null;
  revisionCategoryId?: string | null;
}

export interface ServiceObjectCategoryWithStringTemplateToAdd extends CodebookToAdd {
  customDataTemplate?: string | null;
  revisionCategoryId?: string | null;
}

export interface ServiceObjectCategoryWithStringTemplateToEdit extends CodebookToEdit {
  customDataTemplate?: string | null;
  revisionCategoryId?: string | null;
}

export interface VatOption extends Codebook {
  rate: number;
}

export interface VatOptionToAdd extends CodebookToAdd {
  rate: number;
}

export interface VatOptionToEdit extends CodebookToEdit {
  rate: number;
}

export interface WorkItem {
  id: string;
  created: string;
  modified: string;
  name: string;
  price: number;
  sortOrder: number;
}

export interface WorkItemToAdd {
  name: string;
  price: number;
  sortOrder: number;
}

export interface WorkItemToEdit {
  name: string;
  price: number;
  sortOrder: number;
}

export interface OrderRequestCategory extends Codebook {
  isVisibleCustomerPortal: boolean;
  isVisiblePublicForm: boolean;
}

export interface OrderRequestCategoryToAdd extends CodebookToAdd {
  isVisibleCustomerPortal: boolean;
  isVisiblePublicForm: boolean;
}

export interface OrderRequestCategoryToEdit extends CodebookToEdit {
  isVisibleCustomerPortal?: boolean;
  isVisiblePublicForm?: boolean;
}

export interface CustomCodebook {
  id: string;
  created: string;
  modified: string;
  name: string;
  slug?: string | null;
  allowDynamicManagement: boolean;
  items: CustomCodebookItem[];
}

export interface CustomCodebookItem {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  label: string;
  /** @format int32 */
  sortOrder: number;
  isDeleted: boolean;
}

export interface CustomCodebookToAdd {
  id?: string | null;
  name: string;
  slug: string;
  allowDynamicManagement: boolean;
}

export interface CustomCodebookToEdit {
  name: string;
  slug: string;
  allowDynamicManagement: boolean;
}

export interface CustomCodebookItemToAdd {
  /** @format uuid */
  id?: string | null;
  label: string;
  /** @format int32 */
  sortOrder: number;
}

export interface CustomCodebookItemToEdit {
  label: string;
  /** @format int32 */
  sortOrder: number;
}

export type OrderPriority = 'Low' | 'Normal' | 'High';
export const OrderPriorityArray = ['Low', 'Normal', 'High'];

export type Currency = 'CZK' | 'EUR' | 'USD' | 'GBP';

export const CurrencyArray = ['CZK', 'EUR', 'USD', 'GBP'];

export type LicenceType = 'OneManShow' | 'Business';

export const LicenceTypeArray = ['OneManShow', 'Business'];

export interface TableQuery<TSortBy extends string | number = string | number> {
  page: number;
  pageSize: number;
  sortBy?: TSortBy;
  sortOrder?: SortOrder;
}

export interface TableQueryWithSearchPhrase<TSortBy extends string | number = string | number>
  extends TableQuery<TSortBy> {
  searchPhrase?: string | null;
}

export interface TablePagination {
  current: number;
  pageSize: number;
  total: number;
}

export interface TableResult<T> {
  total: number;
  data: T[];
}

export type FilterValue = (string | number | boolean)[];

export enum SortOrder {
  Ascending = 'ascend',
  Descending = 'descend'
}

export interface UserToUpdateAfterRegister {
  firstName?: string;
  lastName?: string;
}

export interface CountriesState {
  countries: Country[];
  isLoading: boolean;
  error?: string;
}

export interface Country {
  id: string;
  name: string;
  currencyId: string;
  languageId: string;
  timezoneId: string;
}

export interface NotificationToRender {
  id: string;
  title: string;
  message: React.ReactNode;
  seenAt?: string;
  createdAt?: string;
  type: NotificationType;
  data?: Record<string, unknown>;
}

export interface SignalRNotification {
  id: string;
  createdAt: string;
  type: NotificationType;
  title: string;
  message: string;
  data: Record<string, unknown>;
  isVisibleForUser: boolean;
  seenAt?: string;
}

export interface SignalRNotificationList {
  items: SignalRNotification[];
  unreadCount: number;
}

export interface NotificationsState {
  notifications: SignalRNotification[];
  isLoading: boolean;
  error?: string;
  historyTotal: number;
  unreadCount: number;
}

export type NotificationType =
  | 'InterventionAssigned'
  | 'InterventionChanged'
  | 'InterventionConfirmed'
  | 'InterventionDenied'
  | 'InterventionStarted'
  | 'InterventionUnassigned'
  | 'InterventionWaitingToConfirm'
  | 'OrderEditedByCustomer'
  | 'OrderRequestAdded'
  | 'CustomerPortalRequestMessageAdded'
  | 'CustomerPortalRequestAdded'
  | 'CustomerPortalRequestCancelled'
  | 'CustomerPortalRequestResolved'
  | 'SupportTicketCancelled'
  | 'SupportTicketReminder'
  | 'SupportTicketReply'
  | 'SupportTicketResolved'
  | 'CodebooksChanged' // Not visible for user
  | 'CompanySettingsChanged' // Not visible for user
  | 'CompanyConfigurationChanged' // Not visible for user
  | 'FeatureFlagsChanged' // Not visible for user
  | 'UserChanged' // Not visible for user
  | 'AnnouncementChanged' // Not visible for user
  | 'UserDeviceInvalidated' // Not visible for user - logs this device out when it is the invalidated one
  | 'Warning' // Only FE special type
  | 'Info' // Only FE special type
  | 'Outage' // Only for Announcement, only on client
  | 'NewVersion' // Only for Announcement, only on client
  | 'NewFeature' // Only for Announcement, only on client
  | 'Other'; // Only for Announcement, only on client

export type DayOfWeek =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

export const DayOfWeekArray = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

export const DayOfWeekNumberArray = [0, 1, 2, 3, 4, 5, 6];

export interface LanguageState {
  language?: string;
}

export interface CustomForm {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  /** @format int32 */
  codeNumber: number;
  customFormTemplateName: string;
  /** @format uuid */
  customFormTemplateId: string;
  /** @format uuid */
  interventionId?: string | null;
  serviceObjectIds: string[];
  signatures: ReportSignature[];
  isProccessed: boolean;
  isSubmitted: boolean;
  forAssignee: boolean;
  needsIntervention: boolean;
  needsServiceObject: boolean;
  multipleServiceObjects: boolean;
  needsSignature: boolean;
  sendsReports: boolean;
  isRevision: boolean;
  hasLinks: boolean;
  hasMaterial: boolean;
  orderCategoryRestrictions?: string | null;
  serviceObjectCategoryRestrictions?: string | null;
  template: CustomFormDefinition;
  data?: CustomDataObject | null;
  relatedData?: RelatedFieldObject | null;
  /** @format uuid */
  vatOptionId?: string | null;
  vatOptionName?: string | null;
  /** @format double */
  vatRate?: number | null;
  /** @format uuid */
  paymentTypeId?: string | null;
  paymentTypeName?: string | null;
  /** @format uuid */
  priceListId?: string | null;
  priceListName?: string | null;
  canBeEdited: boolean;
  canMaterialBeEdited: boolean;
  /** @format double */
  materialPrice?: number | null;
  /** @format double */
  materialCount?: number | null;
  /** @format double */
  travelPrice?: number | null;
  /** @format int32 */
  travelDistance?: number | null;
  /** @format double */
  workPrice?: number | null;
  /** @format double */
  workHours?: number | null;
}

export interface PublicOrderCustomForm {
  /** @format int32 */
  codeNumber: number;
  /** @format uuid */
  orderId: string;
  /** @format uuid */
  interventionId?: string | null;
  /** @format uuid */
  customFormTemplateId: string;
  serviceObjectIds: string[];
  signatures: ReportSignature[];
  isProccessed: boolean;
  isSubmitted: boolean;
  forAssignee: boolean;
  needsIntervention: boolean;
  needsServiceObject: boolean;
  multipleServiceObjects: boolean;
  needsSignature: boolean;
  orderCategoryRestrictions?: string | null;
  serviceObjectCategoryRestrictions?: string | null;
  template: CustomFormDefinition;
  data?: CustomDataObject | null;
  relatedData?: RelatedFieldObject | null;
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
}

export interface ReportSignature {
  id: string;
  created: string;
  reportId: string;
  serviceObjectId?: string | null;
  publicUrl: string;
  lat: number;
  lng: number;
}

export interface CustomFormDefinition {
  version: string;
  form: FormTemplate;
  restrictions?: FormTemplateRestrictions;
  reports?: ReportTemplate[];
}

export interface FormTemplateRestrictions {
  orderCategory?: string[] | null;
  interventionCategory?: string[] | null;
  serviceObjectCategory?: string[] | null;
}
export interface FormTemplate {
  name: string;
  forAssignee: boolean;
  needsIntervention: boolean;
  needsServiceObject: boolean;
  multipleServiceObjects: boolean;
  sections: CustomDataSection[];
}

export interface TotalCostHidePriceParams {
  material: boolean;
  travelCosts: boolean;
  workTable: boolean;
}

export interface CustomDataTemplate {
  version: string;
  sections: CustomDataSection[];
}

export interface CustomFormCodebooks extends Codebooks {
  users: SelectItem[];
}

export interface CustomDataSection {
  id: string | 'TravelCosts' | 'WorkTable' | 'Material' | 'Signature' | 'TotalCost' | 'CheckList'; // Checklist for backward compatibility
  title: string;
  fields?: CustomDataField[];
  perServiceObject?: boolean;
  isRequired?: boolean;
  hidePrice?: boolean;
  columns?: number;
  serviceObjectCategories?: string[]; // Usinig only with perServiceObject true, show section only for specified service objects
  itemLabel?: string; // Using with Checklist, backward compatibility
  trueLabel?: string; // Using with Checklist, backward compatibility
  falseLabel?: string; // Using with Checklist, backward compatibility
  requireDescriptionWhenFalse?: boolean; // Using with Checklist, backward compatibility
  requireDescriptionWhenTrue?: boolean; // Using with Checklist, backward compatibility
  showIndex?: boolean; // Using with Checklist, backward compatibility
  options?: Option[]; // Using with Checklist, backward compatibility
  description?: FieldValidationType; // Using with WorkTable
  assignee?: FieldValidationType; // Using with WorkTable
  sections?: CustomDataSection[];
  disableCustomMaterial?: boolean; // Using with Material to disable adding custom material
  visibleColumns?: string[]; // Using with Material - format: storeName, categoryName
  groupBy?: MaterialGroupBy; // Using with Material
}

export type FieldValidationType = 'none' | 'required' | 'optional';

export interface CustomDataField {
  id: string;
  label: string;
  type: FormFieldType;
  isRequired: boolean;
  options?: Option[]; // Using with Select, it is possible to set only options or codebook not both. Also used with Checklist
  codebook?: keyof CustomFormCodebooks; // Using with Select, for selecting options from Codebooks + Users, it is possible to set only options or codebook not both, also use with Autocomplete
  unit?: string; // Using with Number
  link?: string;
  defaultValue?: DefaultValue;
  min?: number; // Using with Number
  max?: number; // Using with Number
  isMultiLine?: boolean; // Using with Text
  isMultiSelect?: boolean; // Using with Select, default false
  uiControl?: BooleanUiControlType; // Using with Boolean
  integer?: boolean; // Using with Number, default false
  showIntervalButton?: boolean; // Using with Date
  showBarcodeReader?: boolean; // Using with Text, only working on mobile
  itemLabel?: string; // Using with Checklist
  trueLabel?: string; // Using with Checklist
  falseLabel?: string; // Using with Checklist
  descriptionLabel?: string; // Using with Checklist
  requireDescriptionWhenFalse?: boolean; // Using with Checklist
  requireDescriptionWhenTrue?: boolean; // Using with Checklist
  showIndex?: boolean; // Using with Checklist
}

export type BooleanUiControlType = 'switch' | 'checkbox'; // Maybe Yes or No in future

export type DefaultValueType =
  | 'Static'
  | 'Date'
  | 'Select'
  | 'OrderAddress'
  | 'ContactPersonName'
  | 'AssigneeName';

export interface DefaultValue {
  type: DefaultValueType;
  value?: any;
}

export interface Option {
  id: string;
  label: string;
  isRequired?: boolean;
}

export interface SelectItem {
  id: string;
  name: string;
  disabled?: boolean;
  additionalInfo?: string;
}

export interface TechnicianSelectItem extends SelectItem {
  isDisabled: boolean; // We need to local copy of isDisabled for form validations
}
export interface LabeledValue {
  label: string;
  value: string;
}

export interface CustomDataObject {
  version: string;
  sections: CustomDataSectionValues[];
}

export interface CustomDataSectionValues {
  id: string;
  perServiceObject?: boolean;
  data: CustomDataFieldValue[] | ServiceObjectFieldValue[] | CheckListItemValue[];
}

export interface CustomDataFieldValue {
  id: string;
  value: string | SelectValue[] | CheckListItemValue[];
  stringValue?: string;
  rate?: number; // Vat select input generate rate value
}

export interface ServiceObjectFieldValue {
  serviceObjectId: string;
  fields: CustomDataFieldValue[];
}

export interface MappedSectionWithData {
  id: string;
  title: string;
  fieldsWithData: MappedCustomDataField[];
}

export interface MappedCustomDataField {
  id: string;
  formField: CustomDataField;
  label: string;
  value?: any; // TODO: string | SelectValue[] | CheckListItemValue[];
  stringValue?: string;
  multiplier?: number;
  totalCostData?: TotalCostData; // If field is VatSelect we need to get totalCostData
  format?: string; // for Date/Time and DateTime, use dayjs format
}

export interface MappedCustomDataServiceObjectField {
  id: string;
  formField: CustomDataField;
  label: string;
  multiplier?: number;
  values: MappedCustomDataServiceObjectValues[];
  format?: string; // for Date/Time and DateTime, use dayjs format
}

export interface MappedCustomDataServiceObjectValues {
  serviceObjectId: string;
  value: any;
}

export interface SelectValue {
  value: string | number; // | boolean;
  stringValue: string;
}

export interface VatSelectValue extends SelectValue {
  rate: number;
}

export interface CheckListInputValue {
  value?: string;
  description?: string;
}

export interface CheckListItemValue {
  id: string;
  value?: boolean;
  description?: string;
}

export interface SubmittedCustomData {
  [section: string]: {
    [fieldId: string]: any;
  };
}

export type FormFieldType =
  | 'Text'
  | 'Number'
  | 'Select'
  | 'Boolean'
  | 'Date'
  | 'DateTime'
  | 'Time'
  | 'VatSelect'
  | 'AutoComplete'
  | 'CheckList'
  | 'GeoCoordinate'
  | 'PaymentType';

export type SupportedReportLanguage = 'cs' | 'sk' | 'en' | 'de';
export interface ReportTemplate {
  id: string;
  name: string;
  content: ReportField[];
  needsSignature?: boolean;
  perServiceObject?: boolean;
  language?: SupportedReportLanguage;
}

export interface ReportField {
  type: ReportFieldType;
  properties:
    | FormFieldProps
    | SectionProps
    | RelatedFieldProps
    | CustomBlockProps
    | TextFieldProps
    | ServiceObjectSectionProps; // TODO: other props
}
export type ReportFieldType =
  | 'FormField'
  | 'Empty'
  | 'Section'
  | 'RelatedField'
  | 'CustomBlock'
  | 'TextField'
  | 'ServiceObjectSection';

export interface FormFieldProps {
  fieldId: string;
  sectionId: string;
  label?: string;
  multiplier?: number;
  hideEmpty?: boolean;
  format?: string; // for Date/Time and DateTime, use dayjs format
}

export interface SectionProps {
  title: string;
  fields: ReportField[];
  columns?: number;
  width?: string;
  enablePageBreak?: boolean;
  visibleWhen?: SectionVisibleConditionProps;
  showEmptyTitle?: boolean; // used when we dont know a title but we want to keep spacing
}

export interface SectionVisibleConditionProps {
  conditionType: 'formFieldValue'; // will be more types in the feature
  sectionId: string;
  fieldId: string;
  value: string[];
  strictMode?: boolean; // handle if all values must be present in the data
}

export interface RelatedFieldProps {
  label: string;
  template: string;
  hideEmpty?: boolean;
}

export interface TextFieldProps {
  text: string;
}

export interface ServiceObjectSectionProps {
  displayMode: ServiceObjectSectionDisplayMode;
  title: string;
  fields: ReportField[];
  columns?: number;
  width?: string;
  serviceObjectCategories?: string[]; // if filled show only ServiceObjects matching categories
}

export type ServiceObjectSectionDisplayMode = 'sections' | 'table' | 'tableVertical';

export type CustomBlockType =
  | 'Customer'
  | 'Company'
  | 'Footer'
  | 'Material'
  | 'WorkTable'
  | 'TravelCosts'
  | 'OrderCode'
  | 'CustomerSignature'
  | 'TechnicianSignature'
  | 'Title'
  | 'TotalCost'
  | 'CheckList'
  | 'ServiceObjectsInfo';

export type ServiceObjectsInfoMode = 'count' | 'table';

export type MaterialGroupBy = 'store' | 'category';

export interface CustomBlockProps {
  blockType: CustomBlockType;
  title?: string;
  footerText?: string;
  hidePrice?: boolean;
  withDate?: boolean; // Using with CustomerSignature
  withName?: boolean; // Using with CustomerSignature
  withPosition?: boolean; // Using with CustomerSignature
  withTitle?: boolean;
  customDate?: string; // Using with CustomerSignature
  customName?: string; // Using with CustomerSignature, TechnicianSignature
  customPosition?: string; // Using with CustomerSignature
  customTitle?: string;
  customContent?: HeaderInitials;
  description?: FieldValidationType; // Using with WorkTable
  assignee?: FieldValidationType; // Using with WorkTable
  hideEmpty?: boolean;
  mode?: ServiceObjectsInfoMode; // Currently using only with ServiceObjectsInfo
  showEndCustomerOnly?: boolean; // Using with Customer block
  showInvoiceCustomerOnly?: boolean; // Using with Customer block
  enablePageBreak?: boolean;
  groupBy?: MaterialGroupBy; // Using with Material
  visibleColumns?: string[]; // Using with Material - format: storeName, categoryName
  showFormNumber?: boolean; // Using with OrderCode
  hideStamp?: boolean; // Using with TechnicianSignature
  hideLogo?: boolean; // Using with Company block
}

export interface MappedReportData {
  type: ReportFieldType;
  title?: string;
  columns?: number;
  width?: string;
  fieldData?: MappedCustomDataField | MappedCustomDataServiceObjectField;
  sectionData?: MappedReportData[];
  properties?: RelatedFieldProps;
  blockProps?: CustomBlockProps;
  blockData?: any;
  textValue?: string; // Used with TextField and for RelatedField value
  displayMode?: ServiceObjectSectionDisplayMode;
  serviceObjectCategories?: string[]; // if filled show only ServiceObjects matching categories
  serviceObjectData?: MappedServiceObjectData[];
  checkListProps?: CheckListProps;
  enablePageBreak?: boolean;
  showEmptyTitle?: boolean; // used when we dont know a title but we want to keep spacing
}

export interface MappedServiceObjectData {
  type: ReportFieldType;
  properties?: RelatedFieldProps;
  fieldData?: MappedCustomDataServiceObjectField;
  values?: MappedCustomDataServiceObjectValues[];
  displayMode: ServiceObjectSectionDisplayMode;
  title: string;
  columns?: number;
  width?: string;
  serviceObjectData?: MappedServiceObjectData[];
}

export interface CheckListProps {
  itemLabel?: string; // Using with Checklist
  trueLabel?: string; // Using with Checklist
  falseLabel?: string; // Using with Checklist
  descriptionLabel?: string;
  requireDescriptionWhenFalse?: boolean; // Using with Checklist
  requireDescriptionWhenTrue?: boolean; // Using with Checklist
  showIndex?: boolean; // Using with Checklist
  options?: Option[]; // Using with Checklist
}

export interface RelatedFieldObject {
  company?: LoggedUserCompany;
  customer?: CustomerInOrder;
  order?: OrderDetail;
  intervention?: RelatedDataIntervention;
  primaryAssignee?: User;
  codebooks?: Codebooks;
  invoiceCustomer?: InvoiceCustomerInOrder | null;
}

export interface LinkedDataServiceObjectInCustomer
  extends Omit<ServiceObjectInCustomer, 'revisionCategories'> {
  revisionCategories: Record<string, ServiceObjectsServiceObjectRevisionCategory>;
}

export interface LinkedDataObject {
  customer?: CustomerInOrder;
  order?: OrderDetail;
  intervention?: InterventionDetail;
  serviceObjects?: ServiceObjectInCustomer[];
  serviceObject?: LinkedDataServiceObjectInCustomer;
  users?: User[];
}

export type RelatedFieldTransformType =
  | 'date'
  | 'datetime'
  | 'time'
  | 'currency'
  | 'boolean'
  | 'orderCode'
  | 'oneLineAddress'
  | 'assignations';

export interface EventLogQuery {
  companyId?: string | null;
  page: number;
  pageSize: number;
}

export interface CommandLogQuery {
  companyId: string;
  page: number;
  pageSize: number;
}

export interface EventLog {
  /** @format uuid */
  id: string;

  /** @format date-time */
  createdAt: string;

  /** @format uuid */
  customerId?: string | null;

  /** @format uuid */
  serviceObjectId?: string | null;

  /** @format uuid */
  orderId?: string | null;

  /** @format uuid */
  interventionId?: string | null;

  /** @format uuid */
  userId?: string | null;

  /** @format uuid */
  storeId?: string | null;

  /** @format uuid */
  materialId?: string | null;

  /** @format uuid */
  companyId: string;
  eventName: AuditEventType;
  message?: string | null;
  data?: EventDataObject | null;
  extraData?: Record<string, unknown> | null;
  responsibleUser?: EventUser | null;
}

export interface EventUser {
  Id: string;
  CompanyId: string;
  DeviceId: string;
  FullName: string;
  IsAdmin: boolean;
  IsManager: boolean;
  IsSuperAdmin: boolean;
  IsSystemBot: boolean;
  IsTechnician: boolean;
  Username: string;
}

export interface EventDataObject {
  Id?: string;
  Address?: string;
  FirstName?: string;
  LastName?: string;
  Username?: string;
  Name?: string;
  Email?: string;
  CancelReason?: string;
  OrderCustomFormId?: string;
  Assignee?: Assignee;
  PlannedStart?: string;
  PlannedEnd?: string;
  CodeNumber?: number;
  CodePrefix?: string;
  IsAutoPlanned?: boolean;
  TenantCode?: string;
  IsDisabled?: boolean;
  ImportType?: 'Basic' | 'FromFile' | 'Sample';
}

export interface Assignee {
  Id: string;
  FirstName: string;
  LastName: string;
}

export type EventLogOpenPage =
  | 'Customer'
  | 'ServiceObject'
  | 'Order'
  | 'User'
  | 'UserSettings'
  | 'Company'
  | 'Store'
  | 'Material'
  | 'PriceList';

export enum AuditEventType {
  AddAddress = 'AddAddress',
  EditAddress = 'EditAddress',
  SetInvoiceAddress = 'SetInvoiceAddress',
  SetContactAddress = 'SetContactAddress',
  DeleteAddress = 'DeleteAddress',
  AddCustomer = 'AddCustomer',
  EditCustomer = 'EditCustomer',
  DeleteCustomer = 'DeleteCustomer',
  SetPrimaryContactPerson = 'SetPrimaryContactPerson',
  AddContactPerson = 'AddContactPerson',
  EditContactPerson = 'EditContactPerson',
  DeleteContactPerson = 'DeleteContactPerson',
  AddServiceObject = 'AddServiceObject',
  EditServiceObject = 'EditServiceObject',
  DeleteServiceObject = 'DeleteServiceObject',
  AddOrder = 'AddOrder',
  EditOrder = 'EditOrder',
  CancelOrder = 'CancelOrder',
  DeleteOrder = 'DeleteOrder',
  CompleteOrder = 'CompleteOrder',
  PlanIntervention = 'PlanIntervention',
  ReplanIntervention = 'ReplanIntervention',
  StartIntervention = 'StartIntervention',
  FinishIntervention = 'FinishIntervention',
  CancelIntervention = 'CancelIntervention',
  ConfirmIntervention = 'ConfirmIntervention',
  DenyIntervention = 'DenyIntervention',
  CompleteIntervention = 'CompleteIntervention',
  SendInterventionReport = 'SendInterventionReport',
  SendNextRevisionPlanRequest = 'SendNextRevisionPlanRequest',
  AddUser = 'AddUser',
  EditUser = 'EditUser',
  DeleteUser = 'DeleteUser',
  DisableUser = 'DisableUser',
  RequestUserEmailConfirmation = 'RequestUserEmailConfirmation',
  ChangeUserPassword = 'ChangeUserPassword',
  ChangeUserEmail = 'ChangeUserEmail',
  DeleteUserDevice = 'DeleteUserDevice',
  DisableUserDevice = 'DisableUserDevice',
  SetUserSignature = 'SetUserSignature',
  AddCompany = 'AddCompany',
  EditCompany = 'EditCompany',
  EditCompanySettings = 'EditCompanySettings',
  SetCompanyLogo = 'SetCompanyLogo',
  DeleteCompany = 'DeleteCompany',
  ImportCompanyData = 'ImportCompanyData'
}

export interface AccessLog {
  id: string;
  createdAt: string;
  companyId?: string | null;
  resourceName: string;
  resourceMethod: string;
  parameters?: Record<string, unknown> | null;
}

export interface CommandLog {
  id: string;
  createdAt: string;
  companyId?: string | null;
  commandName: string;
  parameters?: Record<string, unknown> | null;
}

export interface UserLocationIds {
  OrderId?: string | null;
  InterventionId?: string | null;
  ReportId?: string | null;
  ServiceObjectId?: string | null;
  OrderCustomFormId?: string | null;
}

export type LocationType =
  | 'Location'
  | 'InterventionDepartureStarted'
  | 'InterventionStarted'
  | 'ReportSigned'
  | 'InterventionSignaturesConfirmed'
  | 'InterventionFinished';

export interface GeoCoordinate {
  latitude: number;
  longitude: number;
}

export interface DeviceLocationHistoryWithName {
  locations: UserLocationLog[];
  deviceName: string;
}

export interface MappedFieldError {
  error?: string;
  label?: string;
  multipleErrors?: boolean; // Currently using only for Checklist
  errors?: MappedFieldError[]; // Currently using only for Checklist
}

export interface MappedErrorsSection {
  label?: string;
  errors: MappedFieldError[];
}

export type TicketState = 'Open' | 'WaitingForUser' | 'Resolved' | 'Cancelled';

export const TicketStateArray = ['Open', 'WaitingForUser', 'Resolved', 'Cancelled'];

export interface HeaderInitials {
  name: string;
  address: HeaderAddress;
  identificationNumber?: string | null;
  taxNumber?: string | null;
  logoUrl?: string | null; // Logo is always set from user Company, not from report template
}

export interface HeaderAddress {
  street: string;
  city: string;
  postalCode: string;
  country?: string | null;
}

// TODO: it would be nice to rename orderrequests into request_order
export type RenderSettingsScope =
  | 'schedule'
  | 'orders'
  | 'orderrequests'
  | 'customers'
  | 'serviceobjects'
  | 'material'
  | 'storedetail'
  | 'orderadd'
  | 'interventions'
  | 'order_customer'
  | 'order_invoicecustomer'
  | 'order_serviceobjects'
  | 'address_select'
  | 'order_contactperson'
  | 'request_customerportal'
  | 'price_list'
  | 'customforms';

export const RenderSettingsScopesArray: RenderSettingsScope[] = [
  'schedule',
  'orders',
  'orderrequests',
  'customers',
  'serviceobjects',
  'material',
  'storedetail',
  'orderadd',
  'interventions',
  'order_customer',
  'order_invoicecustomer',
  'order_serviceobjects',
  'address_select',
  'order_contactperson',
  'request_customerportal',
  'price_list',
  'customforms'
];

// Schedule using ScheduleConfiguration object
export type RenderSettingsType =
  | 'table'
  | 'detail'
  | 'customerPart'
  | 'serviceObjectsPart'
  | 'invoiceCustomerPart';

export type FormSubmitHandler<T> = (values: T) => Promise<FormSubmitErrorResult | undefined>;
export type HookFormSubmitHandler<T> = (values: T) => Promise<unknown>;

export interface FormSubmitErrorResult {
  errors: Record<string, string>;
}
export interface ReadonlyFormData {
  name: string;
  sections: ReadonlyFormTemplateSection[];
}

export interface ReadonlyFormTemplateSection {
  id: string | 'TravelCosts' | 'WorkTable' | 'Material' | 'Signature' | 'TotalCost' | 'Checklist';
  title?: string;
  fields?: ReadonlyCustomDataField[] | ReadonlyCustomDataServiceObjectField[];
  perServiceObject?: boolean;
  hidePrice?: boolean;
  columns?: number;
  data?: any; // for TravelCosts, WorkTable, Material
  itemLabel?: string; // Using with Checklist, backward compatibility
  trueLabel?: string; // Using with Checklist, backward compatibility
  falseLabel?: string; // Using with Checklist, backward compatibility
  requireDescriptionWhenFalse?: boolean; // Using with Checklist, backward compatibility
  requireDescriptionWhenTrue?: boolean; // Using with Checklist, backward compatibility
  showIndex?: boolean; // Using with Checklist, backward compatibility
  options?: Option[]; // Using with Checklist, backward compatibility
  description?: FieldValidationType; // Using with WorkTable
  assignee?: FieldValidationType; // Using with WorkTable
  sections?: CustomDataSection[];
  disableCustomMaterial?: boolean; // Using with Material to disable adding custom material
  visibleColumns?: string[]; // Using with Material - format: storeName, categoryName
  groupBy?: MaterialGroupBy; // Using with Material
}

export interface ColumnFilterItem {
  text: React.ReactNode;
  value: string | number | boolean;
}

export interface ReadonlyCustomDataField {
  id: string;
  formField: CustomDataField;
  label: string;
  value?: any;
  stringValue?: string;
}

export interface ReadonlyCustomDataServiceObjectField {
  id: string;
  formField: CustomDataField;
  label: string;
  values: MappedCustomDataServiceObjectValues[];
}

// export interface MappedReportData {
//   fieldData?: MappedCustomDataField | MappedCustomDataServiceObjectField;
//   sectionData?: MappedReportData[];
//   properties?: RelatedFieldProps;
//   blockProps?: CustomBlockProps;
//   blockData?: any;
//   textValue?: string;
//   displayMode?: ServiceObjectSectionDisplayMode;
//   serviceObjectData?: MappedReportData[];
// }

export interface CompareDataWithTemplateResult {
  isDifference: boolean;
  data: Record<string, any>;
}

export type ExportFileType = 'xlsx' | 'csv' | 'yaml';
export const ExportFileTypeArray = ['xlsx', 'csv', 'yaml'];

export interface ExportDateParams {
  from: string;
  to: string;
}

export interface ExportFormsParams {
  withForms: boolean;
  withFormData: boolean;
}

export interface CustomFormReport {
  id: string;
  name: string;
  isSigned: boolean;
  needsSignature: boolean;
  perServiceObject: boolean;
  doNotSendReport: boolean;
}

export interface CustomFormReportDocument {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  isPreview: boolean;
  publicUrl: string;
  internalStorageId: string;
  name: string;
  language?: string | null;
  serviceObjectName?: string | null;
}

export interface RenameAttachmentRequest {
  newName: string;
}

// cannot extend Codebook because of the missing sort order
export interface Group {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name: string;
  slug?: string | null;
  isDeleted: boolean;
  users: string[];
}

export interface GroupToAdd extends CodebookToAdd {
  users: string[];
}

export interface GroupToEdit {
  name: string;
  slug?: string | null;
}

// cannot extend Codebook because of the missing sort order
export interface Skill {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name: string;
  slug?: string | null;
  isDeleted: boolean;
  users: string[];
}

export interface SkillToAdd extends CodebookToAdd {
  users: string[];
}

export interface SkillUsersToManage {
  users: string[];
}

export interface GroupUsersToManage {
  users: string[];
}

export interface BasicUser {
  username: string;
  email?: string | null;
  hasEmailConfirmed: boolean;
  phone?: PhoneNumber | null;
  hasPhoneConfirmed: boolean;
  firstName: string;
  lastName: string;
  isDisabled: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isTechnician: boolean;
  isReadonly: boolean;

  /** @format uuid */
  id: string;
}

export interface BasicOrder {
  code: string;
  name?: string | null;
  address: Address;
}

export interface BasicServiceObject {
  name: string;
  identificationNumber?: string | null;
  location?: string | null;
  description?: string | null;
  fullAddress?: string | null;
  address?: Address | null;
}

export interface PowerBiEmbeddedToken {
  token: string;
  /** @format uuid */
  tokenId: string;
  /** @format date-time */
  expiration: string;
}

export interface PowerBiEmbeddedTokenResult {
  token: PowerBiEmbeddedToken;
  report: PowerBiReport;
}

export interface PowerBiReport {
  /** @format uuid */
  id: string;
  reportType: string;
  name: string;
  webUrl: string;
  embedUrl: string;
  /** @format uuid */
  datasetId: string;
  /** @format uuid */
  datasetWorkspaceId: string;
  users: string[];
  subscriptions: string[];
}

export interface FeatureFlagEvaluated {
  name: string;
  isEnabled: boolean;
}
export interface GetFeatureFlagsQueryResult {
  featureFlags: FeatureFlagEvaluated[];
}

export type FeatureFlagType =
  | 'pdf_reports'
  | 'multi_language'
  | 'serviceObject_count'
  | 'serviceObject_timelineSummary'
  | 'customer_portal'
  | 'mapycom_search_provider'
  | 'paymentType_codebook'
  | 'store_permissions'
  | 'custom_forms_table'
  | 'left_menu_sidebar'
  | 'import_export';

export interface Announcement {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  /** @format date-time */
  showFrom: string;
  /** @format date-time */
  showTo: string;
  type: AnnouncementType;
  title: string;
  description: string;
  htmlContent: string;
  isDismissed: boolean;
}

export interface AnnouncementToRender {
  id: string;
  type: AnnouncementType;
  title: string;
  description: React.ReactNode;
  isDismissed: boolean;
}

export type AnnouncementType = 'Info' | 'InfoModal' | 'Warning';

export const AnnouncementTypeArray = ['Info', 'InfoModal', 'Warning'];

export type StyleSize = 'small' | 'medium' | 'big';

export interface CustomFormMaterialTableQuery extends TableQuery {
  storeId?: string | null;
  searchPhrase?: string | null;
}

// Timeline Summary types (used across service objects and potentially other entities)
export interface TimelineSummaryItem {
  finding: string;
  date: string | null;
  orderId: string | null;
  interventionId: string | null;
  customFormId: string | null;
}

export interface TimelineSummaryData {
  items: TimelineSummaryItem[];
  summary: string;
  lastServiceDate: string;
}

export interface ApiErrorDetail {
  title: string; // Obecný statický název chyby (např. "One or more validation errors occurred." nebo "Conflict" nebo "Not Found")
  errorCode?: string | null; // Měl by obsahovat kód chyby, který lze použit pro zobrazené lokalizované chybové hlášky. Může být prázdný. Bude odstraněn a nahrazen errorMessage
  errorMessage?: string | null; // Obsahuje lokalizovanou chybovou hlášku
  detail?: string; // Anglický popis chyby - může se jednat o Exception message - tzn. jedná se o technický detail, který běžný uživatel může ale nemusí pochopit. Mohli bychom ho uživateli zobrazovat někde po kliku na "Zobrazit více"
  status: number; // HTTP status code
  traceId?: string; // OpenTelemetry TraceId - Můžeme uživateli zobrazovat, pokud nám pak pošlou screenshot s chybou nebo její copypaste, ulehčí nám dohledání logů
  spanId?: string; // OpenTelemetry SpanId
  errors?: Record<string, string[]>; // Pouze pro validační chyby, kde klíč je název fieldu/property, která chybu způsobila a hodnota je pole stringů s kódy chyb pro dané pole/property. Jeden field/property může mít více chyb naráz (běžně bude mít ale jen jednu)
}

export type ApiError = AxiosError<ApiErrorDetail>;

export const HttpMethodArray: HttpMethod[] = ['GET', 'POST', 'PUT', 'DELETE'];

export const EntityArray: CustomActionEntity[] = [
  'Customer',
  'ServiceObject',
  'Order',
  'Intervention',
  'OrderRequest',
  'CustomerPortalRequest',
  'Material',
  'Store'
];
